'use client';

import { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { CheckCircle2, FileText, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

export interface SimulatedProgressStep {
  key: string;
  /** Shown while the bar is filling this step's band. */
  label: string;
  /** Upper bound (0–100) of the band this step covers. */
  until: number;
}

/**
 * Narration for a resume upload+parse. The percentages are the band each hint
 * owns — they're for pacing the story, NOT a real measurement.
 */
export const RESUME_UPLOAD_STEPS: SimulatedProgressStep[] = [
  { key: 'upload', label: 'Uploading your resume…', until: 22 },
  { key: 'read', label: 'Reading the document…', until: 45 },
  { key: 'extract', label: 'Extracting your experience…', until: 68 },
  { key: 'skills', label: 'Detecting skills and roles…', until: 86 },
  { key: 'finish', label: 'Finishing up…', until: 97 },
];

/**
 * Narration for reading an already-uploaded resume and filling the profile from
 * it (the onboarding "Fill everything from resume" flow).
 */
export const RESUME_PREFILL_STEPS: SimulatedProgressStep[] = [
  { key: 'read', label: 'Reading your resume…', until: 24 },
  { key: 'understand', label: 'Understanding your experience…', until: 47 },
  { key: 'match', label: 'Matching your profile fields…', until: 70 },
  { key: 'contact', label: 'Filling in contact details…', until: 88 },
  { key: 'save', label: 'Saving to your profile…', until: 97 },
];

// We never claim 100% on our own — the bar eases toward this cap and holds there
// until the real request resolves and the host unmounts us. Fake progress that
// "completes" before the work does is the thing that erodes trust; sitting near
// the end does not.
const CAP = 97;

/**
 * A simulated, self-advancing upload progress. Mount it while an upload/parse is
 * in flight and unmount on completion. It eases a progress bar toward {@link CAP}
 * and narrates plausible stages so the wait feels intentional. This is deliberate
 * imitation — there is no real progress signal from the parse — so it's honest by
 * never reaching 100% itself.
 */
export function SimulatedUploadProgress({
  heading = 'Uploading resume',
  fileName,
  steps = RESUME_UPLOAD_STEPS,
  className,
}: {
  heading?: string;
  fileName?: string;
  steps?: SimulatedProgressStep[];
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (reduceMotion) {
      // Reduced motion: advance through the step bands on a slow timer with
      // discrete jumps — the narration is essential feedback, but skip the
      // continuous tween.
      let i = 0;
      // Seed the first band immediately; subsequent bands advance on the timer.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPct(steps[0]?.until ?? 0);
      const id = setInterval(() => {
        i = Math.min(i + 1, steps.length - 1);
        setPct(steps[i]?.until ?? CAP);
      }, 1400);
      return () => clearInterval(id);
    }

    let raf = 0;
    let last = 0;
    const tick = (t: number) => {
      // Clamp dt so a backgrounded tab doesn't jump the bar on return.
      const dt = last ? Math.min(64, t - last) : 16;
      last = t;
      setPct((prev) => {
        // Asymptotic ease toward CAP: naturally decelerates near the end, so a
        // long parse still reads as steady, unfinished progress.
        const next = prev + (CAP - prev) * (1 - Math.exp(-dt / 900));
        return next > CAP ? CAP : next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduceMotion, steps]);

  const firstUnfilled = steps.findIndex((s) => pct < s.until);
  const activeIdx = firstUnfilled === -1 ? steps.length - 1 : firstUnfilled;
  const rounded = Math.round(pct);

  return (
    <div className={cn('space-y-3 border border-primary/40 bg-primary/[0.04] p-3', className)}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-primary" />
          <p className="truncate text-label font-semibold text-foreground">
            {fileName || heading}
          </p>
        </div>
        <p className="shrink-0 text-caption tabular-nums text-muted-foreground">{rounded}%</p>
      </div>

      <Progress value={rounded} />

      <div className="space-y-1.5">
        {steps.map((step, i) => {
          const state = i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'pending';
          return (
            <div key={step.key} className="flex items-center gap-2 text-caption">
              {state === 'done' ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
              ) : state === 'active' ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
              ) : (
                <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-border" />
              )}
              <span
                className={cn(
                  state === 'active' && 'font-medium text-foreground',
                  state === 'done' && 'text-muted-foreground line-through',
                  state === 'pending' && 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
